'use client';

import { useTour } from '@reactour/tour';
import Link from 'next/link';
import { type FunctionComponent, useState } from 'react';

import LightSwitcherDark from '@/assets/icons/abb_lightswitcher-dark.svg';
import LightSwitcherLight from '@/assets/icons/abb_lightswitcher-light.svg';
import Logo from '@/assets/icons/abb-logo.svg';
import { AbbCaretDown } from '@/assets/icons/components/abb-caret-down';
import { AbbEnvelope } from '@/assets/icons/components/abb-envelope';
import { AbbGlobe } from '@/assets/icons/components/abb-globe';
// import type { Locales } from '@/i18n/get-locales';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { getInitials } from '@/modules/utils/get-initials';
import { Avatar } from '@/ui-components/avatar/avatar';
import { Button } from '@/ui-components/button/button';
import { Card } from '@/ui-components/card/card';
import { Dropdown } from '@/ui-components/dropdown/dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { Toast } from '@/ui-components/toast/toast';
import { Tooltip } from '@/ui-components/tooltip/tooltip';
import { AuthButtonGroup } from '../auth-buttons-group/auth-button-group';
import { BackdropLoading } from '../backdrop/backdrop-loading';
import { Invitations } from '../invitations/invitations';
import { useHeaderParams } from './hooks/use-header-params';
import { type Locales, useAuth } from '@power-core/toolkit';

type HeaderProps = {
	locales: Locales;
	supportedLocales: string[];
	tenant?: string;
	url?: string;
};

export const Header: FunctionComponent<HeaderProps> = ({ locales, supportedLocales, tenant, url }) => {
	const { t, router, pathName, selectedLanguage, isPending, toast, appConfig, isUnacceptedAccess } = useHeaderParams();
	const { addSearchParam } = useNavigation();
	const { setIsOpen, setCurrentStep } = useTour();
	const { isDarkTheme, handleThemeChange } = useTheme();
	const { auth, signOut } = useAuth();
	const [isInvitationsOpen, setIsInvitationsOpen] = useState(false);

	const languages =
		supportedLocales.map((item) => ({
			label: <span className='capitalize px-4 py-2'>{locales[item]?.nativeName}</span>,
			onClick: () => {
				const path = pathName.split('/');
				path[1] = item;
				router.replace(path.join('/'), { scroll: false });
			},
			value: item,
		})) ?? [];

	if (isPending) return <BackdropLoading />;

	const handleSignOut = async () => {
		await signOut();
		window.location.href = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(url ?? '')}`;
	};

	const renderHeader = () => {
		if (auth?.data)
			return (
				<Dropdown
					className='w-[600px]'
					inline
					isMenu
					header={
						<>
							<Paragraph variant='text-lg-medium' className='pt-2'>
								{auth?.data?.user?.name}
							</Paragraph>
							<Paragraph variant='text-md-thin' className='pb-2'>
								{auth?.data?.user?.email}
							</Paragraph>
							<div className='border-y border-n200_background py-1'>
								<div className={`rounded ${isDarkTheme ? '' : 'bg-n200_background'}`}>
									<Flexbox direction='col'>
										<Flexbox justify='between' onClick={() => setIsInvitationsOpen((prev) => !prev)} className='cursor-pointer p-2'>
											<div className='relative'>
												<Paragraph variant={'text-base'}>{t('T_INVITATIONS')}</Paragraph>
												{isUnacceptedAccess && <span className='absolute h-2.5 w-2.5 rounded-full bg-brand -right-3 -top-0' />}
											</div>
											<AbbCaretDown
												size={20}
												fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
												className={`transition-transform duration-200 ${isInvitationsOpen ? 'rotate-0' : '-rotate-90'}`}
											/>
										</Flexbox>
										{isInvitationsOpen && (
											<Flexbox direction='col' className='max-h-[200px] gap-3 overflow-y-scroll scrollbar-rounded pb-2'>
												<Invitations isOpen={isInvitationsOpen} />
											</Flexbox>
										)}
									</Flexbox>
								</div>
							</div>
						</>
					}
					arrowIcon={false}
					label={
						<Avatar
							sizing='medium'
							rounded
							className='w-10'
							placeholderInitials={getInitials(auth?.data?.user?.name ?? '')}
							status={isUnacceptedAccess ? 'busy' : undefined}
							statusPosition={isUnacceptedAccess ? 'top-right' : undefined}
						/>
					}
					menuItems={[
						{ value: 'Sign out', label: <span className='w-full text-left px-4 py-3'>{t('T_SIGN_OUT')}</span>, onClick: handleSignOut },
					]}
				/>
			);

		return <AuthButtonGroup />;
	};

	return (
		<Flexbox>
			<Toast message={toast.message} variant={toast.variant} visible={toast.visible} closeButton />
			<Card fullWidth className='py-4 px-6 bg-transparent relative'>
				<Flexbox direction='row' justify='between' alignContent='center' items='center'>
					<Flexbox justify='center' items='center'>
						<Link href='/'>
							<Logo />
						</Link>
						<div className='h-8 w-[1px] bg-n300_table_background ml-8 mr-4 my-0' />
						<Flexbox direction='col'>
							<Paragraph variant='text-base-medium'>{appConfig?.name}</Paragraph>
							<Paragraph variant='text-base-thin'>{appConfig?.subtitle}</Paragraph>
						</Flexbox>
					</Flexbox>
					<Flexbox items='center' justify='center' className='gap-6'>
						<Button variant='text' onClick={handleThemeChange} className='max-h-10 w-10'>
							{isDarkTheme ? <LightSwitcherLight /> : <LightSwitcherDark />}
						</Button>
						{auth?.data ? (
							<Button
								variant='text'
								sizing='medium'
								className='text-dark dark:text-n700_text_dark_mode !font-medium'
								onClick={() => {
									setCurrentStep(0);
									setIsOpen(true);
								}}
							>
								{t('T_TAKE_TOUR')}
							</Button>
						) : null}
						<Tooltip content={t('T_SUPPORT')} placement='bottom'>
							<AbbEnvelope
								size={24}
								fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'}
								className='cursor-pointer'
								onClick={() => addSearchParam({ key: 'modal', value: 'Support' })}
							/>
						</Tooltip>
						<Flexbox items='center' justify='center' className='w-fit'>
							<Dropdown
								isMenu
								label={
									<Flexbox className='capitalize dark:text-n700_text_dark_mode'>
										<Button
											icon={<AbbGlobe size={22} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} />}
											variant='text'
											sizing='medium'
											className='text-dark dark:text-n700_text_dark_mode !font-medium'
										>
											{locales[selectedLanguage]?.nativeName}
										</Button>
									</Flexbox>
								}
								inline
								arrowIcon
								menuItems={languages}
							/>
						</Flexbox>
						{renderHeader()}
					</Flexbox>
				</Flexbox>
			</Card>
		</Flexbox>
	);
};
